import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PatchMarkerEstimatesRequestBody } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';

interface PatchMarkerEstimatesParams {
  geodataId: string;
  body: PatchMarkerEstimatesRequestBody;
}

export const usePatchMarkerEstimatesMutation = (geodataId: string) => {
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const queryClient = useQueryClient();
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);
  const { t } = useTranslation();
  const [queryId] = useState(geodataId);

  const mutation = useMutation({
    mutationFn: async (params: PatchMarkerEstimatesParams) => {
      return SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().patchMarkerEstimates({
        path: {
          geodataId: params.geodataId,
        },
        body: params.body,
      });
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (_, variables) => {
      // Invalidate the estimates query to refetch updated data
      void queryClient.invalidateQueries({
        queryKey: ['getMarkerEstimates', queryId],
      });

      // Show success message
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('reviewGcpMarkerEstimatesDialog.acceptedAllEstimates', {
          ns: 'cloudProcessing',
          count: variables.body.ids.length,
        }),
      });
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  return {
    patchMarkerEstimates: mutation.mutate,
    patchMarkerEstimatesAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
};
